import { type AdminSubscription, formatUsPhone, queryKeys } from '@mevaser/shared'
import {
  Button,
  EmptyState,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mevaser/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Pencil, Plus, Search, Smartphone, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { AddPlanForCustomer } from '../components/add-plan-for-customer.js'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import {
  CancelSubscriptionDialog,
  chargeShiftText,
  EditSubscriptionDialog,
  planPrice,
  profitText,
  renewalText,
  SubscriptionStatusBadge,
} from '../components/subscription-dialogs.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const DEFAULT_STATUS = 'except_cancelled'

/**
 * Phone plans on customers' accounts (t1083). A plan is bought on an order —
 * New sale in the portal, or the website — and appears here once it's paid for.
 * Staff can also add one directly with Add plan (t1378).
 * Same shape as the rentals list: infinite scroll, not page-number pagination.
 */
export function PlansPage() {
  const navigate = useNavigate()
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(DEFAULT_STATUS)
  const [editing, setEditing] = useState<AdminSubscription | null>(null)
  const [cancelling, setCancelling] = useState<AdminSubscription | null>(null)
  const [adding, setAdding] = useState(false)
  // A sold plan reaches Stripe a few seconds after the payment, so the list is
  // re-read for a short while instead of leaving "not billed by Stripe" up.
  const [watchUntil, setWatchUntil] = useState(0)
  const canWrite = can('subscriptions:write')

  const filters = {
    q: search || undefined,
    status: status === 'all' ? undefined : status,
    pageSize: 50,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.subscriptions.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: AdminSubscription[]; total: number; page: number; pageSize: number }>(
        '/subscriptions',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    refetchInterval: () => (Date.now() < watchUntil ? 3000 : false),
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const columns = useMemo<ColumnDef<AdminSubscription, unknown>[]>(
    () => [
      {
        header: 'Customer',
        accessorKey: 'customerName',
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to="/customers/$customerId"
              params={{ customerId: row.original.customerId }}
              className="block truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.customerName ?? '—'}
            </Link>
            {row.original.customerPhone ? (
              <p className="font-identifier text-xs text-foreground-muted">
                {formatUsPhone(row.original.customerPhone)}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Plan',
        accessorKey: 'planName',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate">{row.original.planName}</p>
            <p className="text-xs text-foreground-muted">{planPrice(row.original)}</p>
          </div>
        ),
      },
      {
        header: 'Line',
        accessorKey: 'phoneLine',
        cell: ({ row }) => (
          <div className="min-w-0">
            {row.original.label ? (
              <p className="truncate text-sm font-medium">{row.original.label}</p>
            ) : null}
            <span className="font-identifier text-sm">
              {row.original.phoneLine ? formatUsPhone(row.original.phoneLine) : '—'}
            </span>
            {row.original.provider ? (
              <p className="truncate text-xs text-foreground-muted">{row.original.provider}</p>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Profit',
        accessorKey: 'profitCents',
        cell: ({ row }) => (
          <span
            className={`whitespace-nowrap text-xs ${
              (row.original.profitCents ?? 0) < 0 ? 'font-medium text-danger' : ''
            }`}
          >
            {profitText(row.original)?.replace('Profit ', '') ?? '—'}
          </span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => <SubscriptionStatusBadge sub={row.original} />,
      },
      {
        header: 'Renewal',
        accessorKey: 'currentPeriodEnd',
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-xs">
            <p className={row.original.status === 'past_due' ? 'font-medium text-danger' : ''}>
              {renewalText(row.original)}
            </p>
            {chargeShiftText(row.original) ? (
              <p className="text-foreground-muted">{chargeShiftText(row.original)}</p>
            ) : null}
            {row.original.billedByStripe ? null : (
              <p className="text-foreground-muted">Billed by hand</p>
            )}
          </div>
        ),
      },
      ...(canWrite
        ? [
            {
              id: 'actions',
              header: '',
              cell: ({ row }: { row: { original: AdminSubscription } }) => (
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit plan"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditing(row.original)
                    }}
                  >
                    <Pencil />
                  </Button>
                  {row.original.status !== 'cancelled' ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Cancel plan"
                      onClick={(e) => {
                        e.stopPropagation()
                        setCancelling(row.original)
                      }}
                    >
                      <XCircle />
                    </Button>
                  ) : null}
                </div>
              ),
            } satisfies ColumnDef<AdminSubscription, unknown>,
          ]
        : []),
    ],
    [canWrite],
  )

  return (
    <Page>
      <PageHeader
        title="Plans"
        description="Phone plans on customers' accounts. Add one here, sell one from New sale, or customers can buy on the website."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              <Plus /> Add plan
            </Button>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer, name, phone line, or plan…"
            className="ps-8"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="except_cancelled">All except cancelled</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="past_due">Past due</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="incomplete">Awaiting payment</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) =>
          void navigate({ to: '/customers/$customerId', params: { customerId: row.customerId } })
        }
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          search || status !== DEFAULT_STATUS ? (
            <EmptyState
              icon={Search}
              variant="no-results"
              title="No plans match"
              description="Try a different search or status."
              onClearFilters={() => {
                setSearch('')
                setStatus(DEFAULT_STATUS)
              }}
            />
          ) : (
            <EmptyState
              icon={Smartphone}
              title="No plans yet"
              description="Plans sold on an order, or added here, appear once paid."
            />
          )
        }
      />

      <AddPlanForCustomer
        open={adding}
        onClose={() => setAdding(false)}
        onSold={() => setWatchUntil(Date.now() + 30_000)}
      />
      <EditSubscriptionDialog sub={editing} onClose={() => setEditing(null)} />
      <CancelSubscriptionDialog sub={cancelling} onClose={() => setCancelling(null)} />
    </Page>
  )
}
